import { ColumnFiltersState } from '@tanstack/react-table';
import { FilterType } from '@tanstack/react-table';
import { AccessorKeyColumnDef, SortingState } from '@tanstack/table-core';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE,
  EMPTY_FILTER_VALUE,
  NULL_FILTER_VALUE,
} from '../constants';
import {
  CrudFilter,
  CrudOperators,
  CrudSort,
  LogicalFilter,
  BaseKey,
} from '../types';

const mapOperatorsFromFilterType = (
  filterType: FilterType
): 'eq' | 'contains' | 'in' | 'between' | 'inci' | 'eqci' => {
  const operators: Record<
    FilterType,
    'eq' | 'contains' | 'in' | 'between' | 'inci' | 'eqci'
  > = {
    select: 'in',
    single_select: 'eq',
    ci_select: 'inci',
    ci_single_select: 'eqci',
    text: 'contains',
    number: 'eq',
    number_range: 'between',
    date: 'between',
  };

  return operators[filterType];
};

const isEmptyFilterValue = (value: unknown): boolean =>
  value === EMPTY_FILTER_VALUE ||
  (Array.isArray(value) &&
    value.length === 1 &&
    value[0] === EMPTY_FILTER_VALUE);

export const mapColumnFiltersToCrudFilters = <T = unknown>(
  columnFilters: ColumnFiltersState,
  columns?: AccessorKeyColumnDef<T>[]
): CrudFilter[] =>
  columnFilters.map(columnFilter => {
    if (isEmptyFilterValue(columnFilter.value))
      return {
        field: columnFilter.id,
        operator: 'isempty',
        value: true,
      };

    const column = columns?.find(c => c.accessorKey === columnFilter.id);

    const filterType = column?.filter?.FilterProps?.type;

    const operator = filterType
      ? mapOperatorsFromFilterType(filterType)
      : mapOperatorsFromFilterType('text');

    return {
      field: columnFilter.id,
      operator: operator,
      value: columnFilter.value,
    };
  });

export const transformOperator = (operator: CrudOperators) => {
  if (operator === 'eq') return 'eq';
  if (operator === 'ne') return 'neq';
  if (operator === 'eqci') return 'eqci';
  if (operator === 'neqci') return 'neqci';
  if (operator === 'gt') return 'gt';
  if (operator === 'gte') return 'gte';
  if (operator === 'lt') return 'lt';
  if (operator === 'lte') return 'lte';
  if (operator === 'contains') return 'like';
  if (operator === 'ncontains') return 'nlike';
  if (operator === 'null') return 'isnull';
  if (operator === 'nnull') return 'isnotnull';
  if (operator === 'isempty') return 'isempty';
  if (operator === 'in') return 'in';
  if (operator === 'nin') return 'nin';
  if (operator === 'inci') return 'inci';
  if (operator === 'ninci') return 'ninc';
  if (operator === 'between') return 'between';

  return 'eq';
};

export const isLogicalFilter = (
  operator: CrudFilter
): operator is LogicalFilter => {
  if (operator.operator === 'and') return false;
  if (operator.operator === 'or') return false;

  return true;
};

const ARRAY_VALUE_OPERATORS: CrudOperators[] = [
  'in',
  'nin',
  'inci',
  'ninci',
  'between',
];

function formatFilterValue(operator: CrudOperators, value: unknown): string {
  const raw = typeof value === 'function' ? value() : value;

  if (!ARRAY_VALUE_OPERATORS.includes(operator)) {
    return raw === null ? NULL_FILTER_VALUE : String(raw ?? '');
  }

  const arr = Array.isArray(raw) ? raw : [raw];

  return JSON.stringify(
    arr.map(item => (item === null ? NULL_FILTER_VALUE : String(item ?? '')))
  );
}

export const getUrl = (resource: string, id?: BaseKey) => {
  if (id) return `api/${resource}/${id}`;

  return `api/${resource}`;
};

export const buildFilter = (filter: CrudFilter) => {
  if (isLogicalFilter(filter)) {
    const valueStr = formatFilterValue(filter.operator, filter.value);

    return `${filter.field}:${transformOperator(filter.operator)}:${valueStr}`;
  }
  // TODO handle conditional operators

  return null;
};

export const buildSorter = (filter: CrudSort) =>
  `${filter.field}:${filter.order}`;

export const buildArraySorter = (filter: CrudSort) => [
  `${filter.field}:${filter.order}`,
];

export const buildQueryFilters = (
  filters?: (CrudFilter | null | false)[]
): string[] =>
  (filters || [])
    .map(filter => filter && buildFilter(filter))
    .filter((item): item is string => !!item);

export const buildQueryArraySorters = (sorters?: CrudSort[]): string[] =>
  (sorters || [])
    .map(sorter => sorter && buildSorter(sorter))
    .filter((item): item is string => !!item);

export const buildQuerySorters = (sorters: CrudSort[]): string =>
  sorters.map(sorter => buildSorter(sorter)).join(',');

export const buildQuerySortingFromState = (state?: SortingState): string => {
  if (!state) return '';

  const sorters = state.map(({ desc, id }) =>
    buildSorter({
      field: id,
      order: desc ? 'desc' : 'asc',
    })
  );

  return sorters.join(',');
};

export const buildQuerySortingFromStateArray = (
  state?: SortingState
): string[] => {
  if (!state) return [];

  return state.map(({ desc, id }) =>
    buildSorter({
      field: id,
      order: desc ? 'desc' : 'asc',
    })
  );
};

export type { CrudFilter };

interface GetAutoCompleteFilterOptions {
  searchTerm: string;
  sortByField?: string;
  filterByField?: string;
}
export const getAutoCompleteFilter = ({
  searchTerm,
  sortByField = 'name',
  filterByField = 'name',
}: GetAutoCompleteFilterOptions) => ({
  page: DEFAULT_PAGE,
  size: DEFAULT_PAGE_SIZE,
  sort: [
    buildSorter({
      field: sortByField,
      order: 'asc',
    }),
  ],
  filter: searchTerm
    ? buildQueryFilters([
        {
          field: filterByField,
          value: searchTerm,
          operator: 'contains',
        },
      ])
    : [],
});
